import { useCallback, useEffect, useState } from 'react'
import type { SortState } from '../../components/mcp/analytics/SortableTh'

/* ─── Per-tab table state: sort, page, page size (MS-700 AC10–12, spec §12) ───

   Kept alongside the filters in sessionStorage, keyed per connection AND per tab,
   because the two tables sort on different columns — persisting one shared sort
   would hand Search Activity a `description` column it does not render.

   This exists because the tabs unmount when you switch away. Without it, "page
   preserved on refresh and tab reload" is quietly false: coming back to Tool
   Invocations from Search Activity would drop the admin on page 1 of a
   differently-sorted table, which reads as the screen having reset itself.

   Page 1 is restored, sort is restored — but a *filter* change still resets to
   page 1, and that reset is the caller's job, because only the caller knows which
   of its own state changes count as a filter.

   **Everything restored is re-validated against the caller's allow-lists.** The
   stored value goes straight into a query string the API validates and rejects
   with a 400, and the tab's error state renders *around* the pager — so a stale
   `sortCol` left over from an earlier build would 400 the query and take the
   *Reset sort* button down with it. There would be no way out of that from the
   UI. `allowed` is a required argument for the same reason: an optional one makes
   the unchecked path the one you get by forgetting. */

export interface TableState {
  sort: SortState
  page: number
  pageSize: number
}

/** What this table will accept back from storage — the caller owns both lists
 *  because it owns the columns it renders and the sizes it offers. */
export interface AllowedTableState {
  sortCols: readonly string[]
  pageSizes: readonly number[]
}

function storageKey(connectionKey: string, tab: string): string {
  return `mcp-analytics:table:${connectionKey}:${tab}`
}

function isPositiveInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
}

function validSort(value: unknown, allowed: AllowedTableState): SortState | null {
  if (typeof value !== 'object' || value === null) return null
  const { col, dir } = value as Partial<SortState>
  if (typeof col !== 'string' || !allowed.sortCols.includes(col)) return null
  if (dir !== 'asc' && dir !== 'desc') return null
  return { col, dir }
}

function read(
  connectionKey: string,
  tab: string,
  fallback: TableState,
  allowed: AllowedTableState,
): TableState {
  try {
    const raw = sessionStorage.getItem(storageKey(connectionKey, tab))
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<TableState>
    // Each field falls back independently: one unrecognised value should cost you
    // that value, not your page and page size as well.
    return {
      sort: validSort(parsed.sort, allowed) ?? fallback.sort,
      page: isPositiveInt(parsed.page) ? parsed.page : fallback.page,
      pageSize:
        isPositiveInt(parsed.pageSize) && allowed.pageSizes.includes(parsed.pageSize)
          ? parsed.pageSize
          : fallback.pageSize,
    }
  } catch {
    return fallback
  }
}

export function useTableState(
  connectionKey: string,
  tab: string,
  fallback: TableState,
  allowed: AllowedTableState,
) {
  const [state, setState] = useState<TableState>(() =>
    read(connectionKey, tab, fallback, allowed),
  )

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey(connectionKey, tab), JSON.stringify(state))
    } catch {
      // Storage full or blocked: the table still works for this session, it just
      // does not survive a tab switch. Not worth surfacing.
    }
  }, [connectionKey, tab, state])

  const setSort = useCallback((next: SortState) => {
    // Any sort change is a new question, so it starts at page 1 (spec §12).
    setState(prev => ({ ...prev, sort: next, page: 1 }))
  }, [])

  const setPage = useCallback((page: number) => {
    setState(prev => ({ ...prev, page }))
  }, [])

  const setPageSize = useCallback((pageSize: number) => {
    setState(prev => ({ ...prev, pageSize, page: 1 }))
  }, [])

  /** Back to page 1 without touching the sort — for a filter change. */
  const resetPage = useCallback(() => {
    setState(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [])

  return { ...state, setSort, setPage, setPageSize, resetPage }
}
